import Plotly from "plotly.js-basic-dist";

/** Multiply axis title + tick font sizes only while generating a PDF raster. */
export const PDF_AXIS_FONT_SCALE = 1.14;

/**
 * @param {object} layout — Plotly `gd.layout`
 * @returns {Record<string, number>} Plotly relayout keys -> original font sizes
 */
export function snapshotAxisFontSizesForPdfRevert(layout) {
  /** @type {Record<string, number>} */
  const revert = {};
  if (!layout || typeof layout !== "object") return revert;
  for (const axisKey of Object.keys(layout)) {
    if (!/^[xy]axis/.test(axisKey)) continue;
    const ax = layout[axisKey];
    if (!ax || typeof ax !== "object") continue;
    const tickSize = ax.tickfont?.size;
    if (typeof tickSize === "number" && Number.isFinite(tickSize)) {
      revert[`${axisKey}.tickfont.size`] = tickSize;
    }
    const title = ax.title;
    if (title && typeof title === "object") {
      const titleSize = title.font?.size;
      if (typeof titleSize === "number" && Number.isFinite(titleSize)) {
        revert[`${axisKey}.title.font.size`] = titleSize;
      }
    }
  }
  return revert;
}

/**
 * @param {object} layout — Plotly `gd.layout`
 * @param {number} factor
 * @returns {Record<string, number>}
 */
export function buildAxisFontBoostRelayout(layout, factor = PDF_AXIS_FONT_SCALE) {
  /** @type {Record<string, number>} */
  const patch = {};
  if (!layout || typeof layout !== "object") return patch;
  for (const axisKey of Object.keys(layout)) {
    if (!/^[xy]axis/.test(axisKey)) continue;
    const ax = layout[axisKey];
    if (!ax || typeof ax !== "object") continue;
    const tickSize = ax.tickfont?.size;
    if (typeof tickSize === "number" && Number.isFinite(tickSize)) {
      patch[`${axisKey}.tickfont.size`] = Math.max(8, Math.round(tickSize * factor));
    }
    const title = ax.title;
    if (title && typeof title === "object") {
      const titleSize = title.font?.size;
      if (typeof titleSize === "number" && Number.isFinite(titleSize)) {
        patch[`${axisKey}.title.font.size`] = Math.max(10, Math.round(titleSize * factor));
      }
    }
  }
  return patch;
}

export function nextFrame() {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });
}

/**
 * Temporarily enlarges axis fonts for PDF capture. Never throws — export continues if relayout fails.
 * @param {HTMLElement | null | undefined} gd Plotly graph div (`plotRef.current.el`)
 * @returns {Promise<() => Promise<void>>} call to restore original font sizes
 */
export async function applyPdfAxisFontBoost(gd) {
  const noop = async () => {};
  if (!gd?.layout || typeof gd.layout !== "object") return noop;

  const revert = snapshotAxisFontSizesForPdfRevert(gd.layout);
  const boost = buildAxisFontBoostRelayout(gd.layout);
  if (!Object.keys(boost).length) return noop;

  try {
    await Plotly.relayout(gd, boost);
    await nextFrame();
  } catch (err) {
    console.warn("PDF axis font boost skipped:", err);
    return noop;
  }

  return async () => {
    if (!Object.keys(revert).length) return;
    try {
      await Plotly.relayout(gd, revert);
    } catch (err) {
      console.warn("PDF axis font revert skipped:", err);
    }
  };
}
